/**
 * The public landing page.
 *
 * It has three jobs and they are not equal: sell the service to a viewer,
 * offer distribution to a film-maker, and offer inventory to an advertiser.
 * Viewers lead because they are the broadest audience and the only one that
 * can convert without a conversation; the other two get their own full-width
 * sections rather than a line in the footer.
 *
 * Everything here resolves to something real. The primary path is /signup,
 * which starts a genuine fourteen-day trial with no payment details. The
 * television path is /link, the device-pairing page the Roku channel talks to.
 * Prices come from the billing service at runtime. Creator and advertiser
 * enquiries go to configured addresses, because this platform has no partner
 * intake endpoint and a form posting into nothing would be a lie told in HTML.
 *
 * Motion is CSS only and consists of one idea - content rises slightly as it
 * enters - implemented with an IntersectionObserver that adds a class. It is
 * disabled wholesale under `prefers-reduced-motion`, and because the reveal is
 * an *addition* to already-visible content rather than a gate on it, a visitor
 * with JavaScript disabled or an observer that never fires still reads
 * everything.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Art } from "@/components/landing/Art";
import { BrandMark } from "@/components/landing/BrandMark";
import { HeroArt } from "@/components/landing/HeroArt";
import { LandingHeader } from "@/components/landing/LandingHeader";
import { PlanGrid } from "@/components/landing/PlanGrid";
import {
  ADVERTISER_AUDIENCES,
  CREATOR_AUDIENCES,
  FAQ,
  PLATFORM_FACTS,
  SHOWCASE,
  VIEWER_BENEFITS,
} from "@/components/landing/content";
import "@/styles/landing.css";

// Both default to the same mailbox today. They stay two separate variables
// because they answer two different audiences and will very likely diverge:
// splitting them later is a deployment setting, not a code change.
const DISTRIBUTION_EMAIL = import.meta.env.VITE_DISTRIBUTION_EMAIL || "contacts@hayfish.ai";
const ADVERTISING_EMAIL = import.meta.env.VITE_ADVERTISING_EMAIL || "contacts@hayfish.ai";

function mailto(address: string, subject: string): string {
  return `mailto:${address}?subject=${encodeURIComponent(subject)}`;
}

/** Reveal-on-scroll, added as an enhancement and removed under reduced motion. */
function useReveal() {
  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (reduced?.matches || typeof IntersectionObserver === "undefined") return;

    const targets = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    targets.forEach((target) => target.setAttribute("data-reveal", "pending"));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute("data-reveal", "shown");
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);
}

export function Landing() {
  useReveal();

  return (
    <div className="landing" id="top">
      <LandingHeader />

      <main id="main">
        {/* ------------------------------------------------------------ hero */}
        <section className="lhero" aria-labelledby="lhero-title">
          <div className="lhero__art">
            <HeroArt />
            <div className="lhero__scrim" />
          </div>

          <div className="lhero__body">
            <p className="eyebrow">
              <BrandMark size={18} />
              <span>Streaming, and the platform underneath it</span>
            </p>
            <h1 id="lhero-title" className="lhero__title">
              Entertain the world.
              <br />
              Write the next episode.
            </h1>
            <p className="lhero__lede">
              Hayfish is a streaming service that understands what it is showing you. Ask for the
              evening you want, get a reason with every recommendation, and pick the story back up
              on whatever screen you are near.
            </p>
            <div className="cta-row">
              <Link to="/signup" className="btn btn--accent btn--lg">
                Start free trial
              </Link>
              <Link to="/signin" className="btn btn--ghost btn--lg">
                Sign in
              </Link>
            </div>
            <p className="lhero__terms">
              Fourteen days free. No payment details to begin. Cancel from your account at any time.
            </p>
          </div>

          <ul className="lhero__doors" aria-label="Three ways into Hayfish">
            <li>
              <a href="#watch">
                <span className="lhero__door-label">Watch</span>
                <span className="lhero__door-body">
                  Films, series and originals, on the web and on your television.
                </span>
              </a>
            </li>
            <li>
              <a href="#creators">
                <span className="lhero__door-label">Distribute</span>
                <span className="lhero__door-body">
                  Bring a film or a series to a service built to introduce it properly.
                </span>
              </a>
            </li>
            <li>
              <a href="#advertisers">
                <span className="lhero__door-label">Advertise</span>
                <span className="lhero__door-body">
                  Put your commercial inside an evening people chose, not beside it.
                </span>
              </a>
            </li>
          </ul>
        </section>

        {/* ------------------------------------------------------- discovery */}
        <section className="section" id="watch" aria-labelledby="watch-title">
          <div className="section__head section__head--wide" data-reveal>
            <p className="eyebrow eyebrow--plain">On Hayfish now</p>
            <h2 id="watch-title">A catalog small enough to have a point of view</h2>
            <p className="section__lede">
              Films, series and documentaries, including Hayfish Originals made for this service and
              nowhere else. Every title arrives already understood, so the thing you are in the mood
              for is a sentence away rather than a genre menu away.
            </p>
          </div>

          <div
            className="showcase"
            tabIndex={0}
            role="group"
            aria-label="Titles in the Hayfish catalog, scrollable"
          >
            <ul className="showcase__track">
              {SHOWCASE.map((title) => (
                <li key={title.slug} className="showcase__item">
                  <div className="showcase__art">
                    <Art
                      base={`/posters/${title.slug}`}
                      width={300}
                      height={450}
                      sizes="(max-width: 700px) 34vw, 172px"
                      alt={`Key art for ${title.name}`}
                    />
                    {title.original && <span className="showcase__badge">Original</span>}
                  </div>
                  <p className="showcase__name">{title.name}</p>
                  <p className="showcase__meta">
                    {title.year} · {title.rating} · {title.genre}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <p className="showcase__note">
            Titles, synopses and key art are original works made for Hayfish.
          </p>
        </section>

        {/* -------------------------------------------------- viewer benefits */}
        <section className="section section--tint" aria-labelledby="why-title">
          <div className="section__head" data-reveal>
            <p className="eyebrow eyebrow--plain">Why viewers stay</p>
            <h2 id="why-title">Less hunting. More watching.</h2>
          </div>
          <ul className="benefits">
            {VIEWER_BENEFITS.map((benefit) => (
              <li key={benefit.title} className="benefit" data-reveal>
                <h3>{benefit.title}</h3>
                <p>{benefit.body}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* ------------------------------------------------------------ plans */}
        <section className="section" id="plans" aria-labelledby="plans-title">
          <div className="section__head section__head--wide" data-reveal>
            <p className="eyebrow eyebrow--plain">Plans</p>
            <h2 id="plans-title">Pick one. Change it whenever you like.</h2>
            <p className="section__lede">
              Prices come straight from the Hayfish billing service, so what is printed here is what
              would be charged. Every account starts on a fourteen-day trial first.
            </p>
          </div>
          <PlanGrid />
        </section>

        {/* ---------------------------------------------------------- devices */}
        <section className="section split" aria-labelledby="devices-title">
          <div className="split__body" data-reveal>
            <p className="eyebrow eyebrow--plain">Where it plays</p>
            <h2 id="devices-title">Open a browser. Or just turn on the television.</h2>
            <p>
              The Hayfish web player runs in any modern browser, and the Hayfish channel puts the
              same catalog on Roku. Both talk to the same platform, so a title that appears on one
              appears on the other, and your place in an episode is the same place on both.
            </p>
            <p>
              Pairing a television takes a code and no typing of passwords on a remote: open the
              channel, read the code it shows, and enter it here.
            </p>
            <div className="cta-row">
              <Link to="/link" className="btn btn--primary">
                Connect a television
              </Link>
              <Link to="/signup" className="btn btn--ghost">
                Create an account first
              </Link>
            </div>
          </div>
          <div className="split__art split__art--devices" data-reveal>
            <Art
              base="/landing/devices"
              widths={[1200, 760]}
              width={1200}
              height={675}
              sizes="(max-width: 900px) 92vw, 46vw"
              alt="A dark living room at night lit by the glow of a large wall-mounted television"
            />
          </div>
        </section>

        {/* --------------------------------------------------------- creators */}
        <section className="section split split--reverse" id="creators" aria-labelledby="creators-title">
          <div className="split__art split__art--creators" data-reveal>
            <Art
              base="/landing/creators"
              widths={[1200, 760]}
              width={1200}
              height={675}
              sizes="(max-width: 900px) 92vw, 46vw"
              alt="A small film crew working a night exterior: a camera operator on a dolly, a boom operator and a lighting technician"
            />
          </div>
          <div className="split__body" data-reveal>
            <p className="eyebrow">
              <span className="eyebrow__rule" aria-hidden="true" />
              <span>For creators</span>
            </p>
            <h2 id="creators-title">Work that needs finding deserves a service that can find it</h2>
            <p>
              Most catalogs bury a careful film under whatever launched that week. Hayfish reads
              every title on ingest for its themes, its mood and who it is for, and that reading is
              what drives discovery. A documentary about repairing washing machines reaches the
              people who will love it because the platform knows what it is, not because it bought
              a row.
            </p>
            <p>
              Distribution here is a real pipeline, not an upload box: technical delivery into the
              encoding ladder, a title page written properly, and a launch that lands on the web
              player and the Roku channel at the same time.
            </p>

            <ul className="tags" aria-label="Creators Hayfish works with">
              {CREATOR_AUDIENCES.map((audience) => (
                <li key={audience} className="chip">
                  {audience}
                </li>
              ))}
            </ul>

            <div className="cta-row">
              <a
                className="btn btn--accent"
                href={mailto(DISTRIBUTION_EMAIL, "Distribution enquiry")}
              >
                Distribute on Hayfish
              </a>
              <a className="btn btn--quiet" href="#questions">
                Read the distribution questions
              </a>
            </div>
            <p className="note">
              Opens your email client, addressed to <span className="note__address">{DISTRIBUTION_EMAIL}</span>.
              Terms, licensing and revenue share are agreed title by title; we publish no standard
              terms and will not quote any until we have seen the work. What you send is used only
              to answer you.
            </p>
          </div>
        </section>

        {/* ------------------------------------------------------ advertisers */}
        <section className="section split" id="advertisers" aria-labelledby="advertisers-title">
          <div className="split__body" data-reveal>
            <p className="eyebrow">
              <span className="eyebrow__rule" aria-hidden="true" />
              <span>For advertisers</span>
            </p>
            <h2 id="advertisers-title">A commercial is a short film. Give it a room worth playing in.</h2>
            <p>
              Hayfish sells an ad-supported plan, which means the advertising lives inside an
              evening somebody actively chose - full screen, sound on, in the middle of a story they
              came for. That is a different proposition from a banner beside a feed, and it asks
              more of the work.
            </p>
            <p>
              Campaigns are arranged with our team: the placement, the format, the flight, and an
              honest account of what we can and cannot measure for you. There is no self-serve
              console yet. We would rather say that than sell you one.
            </p>

            <ul className="tags" aria-label="Advertisers Hayfish works with">
              {ADVERTISER_AUDIENCES.map((audience) => (
                <li key={audience} className="chip">
                  {audience}
                </li>
              ))}
            </ul>

            <div className="cta-row">
              <a
                className="btn btn--accent"
                href={mailto(ADVERTISING_EMAIL, "Advertising enquiry")}
              >
                Advertise with Hayfish
              </a>
              <a className="btn btn--quiet" href="#questions">
                Read the advertising questions
              </a>
            </div>
            <p className="note">
              Opens your email client, addressed to <span className="note__address">{ADVERTISING_EMAIL}</span>.
              We publish no reach figures, impression estimates or audience demographics, because we
              will not quote a number we cannot stand behind. Ask, and we will tell you exactly what
              is measurable today. What you send is used only to answer you.
            </p>
          </div>
          <div className="split__art split__art--advertisers" data-reveal>
            <Art
              base="/landing/advertisers"
              widths={[1200, 760]}
              width={1200}
              height={675}
              sizes="(max-width: 900px) 92vw, 46vw"
              alt="An empty commercial film stage lit in cyan and magenta, with a single crew member beside a light stand"
            />
          </div>
        </section>

        {/* --------------------------------------------------------- platform */}
        <section className="section section--tint" aria-labelledby="platform-title">
          <div className="section__head" data-reveal>
            <p className="eyebrow eyebrow--plain">Underneath</p>
            <h2 id="platform-title">Built like infrastructure, not like a website with videos on it</h2>
          </div>
          <ul className="facts">
            {PLATFORM_FACTS.map((fact) => (
              <li key={fact.title} className="fact" data-reveal>
                <h3>{fact.title}</h3>
                <p>{fact.body}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* -------------------------------------------------------------- faq */}
        <section className="section" id="questions" aria-labelledby="faq-title">
          <div className="section__head" data-reveal>
            <p className="eyebrow eyebrow--plain">Questions</p>
            <h2 id="faq-title">The things people ask first</h2>
          </div>
          <div className="faq">
            {FAQ.map((entry) => (
              <details key={entry.question} className="faq__item">
                <summary>
                  <span>{entry.question}</span>
                  <span className="faq__marker" aria-hidden="true" />
                </summary>
                <p>{entry.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ----------------------------------------------------------- finale */}
        <section className="finale" aria-labelledby="finale-title">
          <Art
            base="/landing/finale"
            widths={[1920, 1280, 800]}
            width={1920}
            height={1080}
            sizes="100vw"
            className="finale__img"
            alt=""
          />
          <div className="finale__scrim" />
          <div className="finale__body" data-reveal>
            <h2 id="finale-title">Fourteen days. Then you decide.</h2>
            <p>
              No payment details to begin, no contract, and an account page that cancels in one
              action. The only thing we are asking for tonight is your attention.
            </p>
            <div className="cta-row">
              <Link to="/signup" className="btn btn--accent btn--lg">
                Create your account
              </Link>
              <Link to="/link" className="btn btn--ghost btn--lg">
                Connect a television
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* -------------------------------------------------------------- foot */}
      <footer className="lfooter">
        <div className="lfooter__grid">
          <div className="lfooter__brand">
            <a href="#top" className="lfooter__mark">
              <BrandMark size={26} />
              <span>Hayfish</span>
            </a>
            <p>Entertain the world. Write the next episode.</p>
          </div>

          <nav aria-label="Watch">
            <h2>Watch</h2>
            <ul>
              <li>
                <Link to="/signup">Create an account</Link>
              </li>
              <li>
                <Link to="/signin">Sign in</Link>
              </li>
              <li>
                <Link to="/link">Connect a television</Link>
              </li>
              <li>
                <a href="#plans">Plans and pricing</a>
              </li>
            </ul>
          </nav>

          <nav aria-label="Partners">
            <h2>Partners</h2>
            {/* These names deliberately differ from the section buttons above.
                Two links with one accessible name and two destinations is a
                trap in a screen reader's link list. */}
            <ul>
              <li>
                <a href="#creators">For creators</a>
              </li>
              <li>
                <a href={mailto(DISTRIBUTION_EMAIL, "Distribution enquiry")}>Distribution enquiries</a>
              </li>
              <li>
                <a href="#advertisers">For advertisers</a>
              </li>
              <li>
                <a href={mailto(ADVERTISING_EMAIL, "Advertising enquiry")}>Advertising enquiries</a>
              </li>
            </ul>
          </nav>

          <nav aria-label="About Hayfish">
            <h2>About</h2>
            <ul>
              <li>
                <a href="#watch">The catalog</a>
              </li>
              <li>
                <a href="#questions">Questions</a>
              </li>
              <li>
                <a href={mailto(DISTRIBUTION_EMAIL, "Press enquiry")}>Press</a>
              </li>
            </ul>
          </nav>
        </div>

        <div className="lfooter__base">
          {/* No dead links to unwritten policies: the pages that do not exist yet are
              named as not existing yet, which is the only honest version of a legal
              footer on a platform that has not published its terms. */}
          <p>© {new Date().getFullYear()} Hayfish. All rights reserved.</p>
          <p>
            Every title, synopsis, person and piece of artwork in the Hayfish catalog is an original
            work created for this platform. Terms of service and the privacy notice are published
            with the first public release; until then, enquiries above reach a person.
          </p>
        </div>
      </footer>
    </div>
  );
}
